import React, { useState, useEffect } from 'react';
import { useAuth } from 'shared-auth';
import { useNotification } from './NotificationSystem';
import * as pushService from 'shared-push';
import { TestProtocol } from '../types';
import { BellIcon, BellOffIcon, AlertTriangleIcon } from 'shared-icons';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  testProtocol: TestProtocol;
  setTestProtocol: (protocol: TestProtocol) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, testProtocol, setTestProtocol }) => {
  // FIX: Removed 'token' from useAuth destructuring. Authentication is cookie-based.
  const { isAuthenticated } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const notify = useNotification();

  useEffect(() => {
    if (isOpen) {
      setIsSupported(pushService.isPushSupported());
      if (pushService.isPushSupported()) {
          pushService.isSubscribed().then(setIsSubscribed);
      }
    }
  }, [isOpen]);

  const handleToggleSubscription = async () => {
    if (!isSupported || isToggling) return;
    setIsToggling(true);

    try {
      if (isSubscribed) {
        await pushService.unsubscribeUser();
        setIsSubscribed(false);
        notify({ type: 'info', title: 'Notifications Disabled', message: 'You will no longer receive notifications.' });
      } else {
        const subscription = await pushService.subscribeUser();
        if (subscription) {
            // FIX: Removed 'token' argument. The API call is authenticated by the browser sending the httpOnly cookie.
            await pushService.sendSubscriptionToBackend(subscription);
            setIsSubscribed(true);
            notify({ type: 'success', title: 'Notifications Enabled!', message: 'You will now receive occasional updates.' });
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred.';
      notify({ type: 'error', title: 'Could not update setting', message });
    } finally {
      setIsToggling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="settings-title" style={{ animation: 'modal-fade-in 0.3s' }}>
      <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} style={{ animation: 'modal-content-pop 0.3s' }}>
        <div className="flex justify-between items-center mb-4">
          <h2 id="settings-title" className="text-xl font-bold text-slate-900 dark:text-white">Settings</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white text-3xl leading-none" aria-label="Close modal">&times;</button>
        </div>
        
        <div className="space-y-6">
          {/* Test Protocol Setting */}
          <div className="bg-gray-100 dark:bg-slate-700/50 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">Test Protocol</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">WebSocket can be more efficient, while HTTP is the standard. Your preference will be saved.</p>
              <div className="flex rounded-full p-1 bg-gray-200 dark:bg-slate-700">
                  <button
                      onClick={() => setTestProtocol('http')}
                      className={`flex-1 py-2 px-4 text-center rounded-full font-semibold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${testProtocol === 'http' ? 'bg-cyan-600 text-white shadow' : 'text-slate-600 dark:text-slate-300 hover:bg-gray-300/50 dark:hover:bg-slate-600/50'}`}
                      aria-pressed={testProtocol === 'http'}
                  >
                      HTTP
                  </button>
                   <button
                      onClick={() => setTestProtocol('websocket')}
                      className={`flex-1 py-2 px-4 text-center rounded-full font-semibold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${testProtocol === 'websocket' ? 'bg-cyan-600 text-white shadow' : 'text-slate-600 dark:text-slate-300 hover:bg-gray-300/50 dark:hover:bg-slate-600/50'}`}
                      aria-pressed={testProtocol === 'websocket'}
                  >
                      WebSocket
                  </button>
              </div>
          </div>
          
          {/* Push Notification Setting */}
          {!isSupported ? (
            <div className="flex items-start gap-3 text-sm text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-md">
                <AlertTriangleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <p>Push notifications are not supported by your browser or are disabled in your browser settings.</p>
            </div>
          ) : !isAuthenticated ? (
            <div className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/50 p-3 rounded-md">
                 <BellOffIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                 <p>Please log in to the <a href="https://teztel.com/login" target="_blank" rel="noopener noreferrer" className="text-cyan-500 underline">TeezNet site</a> to enable personalized notifications.</p>
            </div>
          ) : (
            <div className="flex justify-between items-center bg-gray-100 dark:bg-slate-700/50 p-4 rounded-lg">
              <div>
                <h3 className="font-semibold text-slate-800 dark:text-slate-200">Push Notifications</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">Receive performance alerts & updates.</p>
              </div>
              <button 
                onClick={handleToggleSubscription}
                disabled={isToggling}
                className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 ${isSubscribed ? 'bg-cyan-600' : 'bg-gray-300 dark:bg-slate-600'}`}
                role="switch"
                aria-checked={isSubscribed}
              >
                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${isSubscribed ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          )}
        </div>
        
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-6 py-2 bg-gray-200 dark:bg-slate-700 text-slate-800 dark:text-white font-semibold rounded-full hover:bg-gray-300 dark:hover:bg-slate-600/50 transition">Done</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;